// Copyright (c) 2026 PGNS LLC

import { Link } from '@tanstack/react-router';
import { Badge } from '@pgns/ui/badge';
import type { Incident } from '@/lib/api';
import { cn } from '@/lib/utils';
import { formatTimestamp, impactFill, impactLabel, incidentDuration } from '@/lib/status';

/**
 * One incident with its updates, newest first.
 *
 * Markdown bodies are rendered as preformatted text rather than parsed HTML:
 * incident copy arrives from a D1 row written by a webhook and a CI job, and no
 * status page should be the thing that renders untrusted markup.
 */
export function IncidentEntry({
  incident,
  compact = false,
}: {
  incident: Incident;
  compact?: boolean;
}) {
  const updates = compact ? incident.updates.slice(0, 1) : incident.updates;
  return (
    <article className="relative pl-5">
      <span
        aria-hidden="true"
        className={cn('absolute left-0 top-2 size-2 rounded-full', impactFill(incident.impact))}
      />
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="font-medium text-foreground">
          <Link
            to="/incidents/$incidentId"
            params={{ incidentId: incident.id }}
            className="hover:underline"
          >
            {incident.title}
          </Link>
        </h3>
        <Badge variant={incident.resolved ? 'secondary' : 'warning'}>
          {incident.resolved ? 'Resolved' : incident.status}
        </Badge>
        {incident.auto && <Badge variant="outline">Detected automatically</Badge>}
      </div>
      <p className="text-xs text-muted-foreground mt-1">
        {impactLabel(incident.impact)} · started {formatTimestamp(incident.started_at)} ·{' '}
        {incidentDuration(incident)}
        {incident.components.length > 0 && ` · ${incident.components.join(', ')}`}
      </p>
      {updates.length > 0 && (
        <ul className="mt-3 space-y-3">
          {updates.map((update) => (
            <li key={`${update.ts}-${update.status}`} className="text-sm">
              <p className="text-xs font-mono text-muted-foreground">
                {formatTimestamp(update.ts)} · {update.status} · {update.author}
              </p>
              <p className="whitespace-pre-wrap text-foreground">{update.body_md}</p>
            </li>
          ))}
        </ul>
      )}
      {compact && incident.updates.length > 1 && (
        <Link
          to="/incidents/$incidentId"
          params={{ incidentId: incident.id }}
          className="mt-2 inline-block text-xs text-primary hover:underline"
        >
          {incident.updates.length - 1} earlier update
          {incident.updates.length - 1 === 1 ? '' : 's'}
        </Link>
      )}
    </article>
  );
}

export function IncidentTimeline({
  incidents,
  compact = false,
  emptyMessage,
}: {
  incidents: Incident[];
  compact?: boolean;
  emptyMessage: string;
}) {
  if (incidents.length === 0) {
    return <p className="text-sm text-muted-foreground">{emptyMessage}</p>;
  }
  return (
    <div className="space-y-8 border-l border-border pl-3">
      {incidents.map((incident) => (
        <IncidentEntry key={incident.id} incident={incident} compact={compact} />
      ))}
    </div>
  );
}
